import '../../../../shared/components/ui/Shared.css';
import './UserPortal.css';

const ALERT_COPY = {
  WAITING: {
    tone: 'waiting',
    chip: 'Waiting',
    title: 'You are in the queue',
    message: 'Keep shopping if you like. We will keep this page updated and also send fallback SMS or WhatsApp alerts.'
  },
  ONE_AHEAD: {
    tone: 'oneAhead',
    chip: '1 Ahead',
    title: 'One person ahead of you',
    message: 'Please head closer to the counter now so you do not miss your turn.'
  },
  TURN_NOW: {
    tone: 'turnNow',
    chip: 'Turn Now',
    title: 'It is your turn now',
    message: 'Go to the counter immediately. If you are already there, confirm your presence as soon as possible.'
  },
  TURN_REMINDER: {
    tone: 'turnReminder',
    chip: 'Final Call',
    title: 'Final call for your turn',
    message: 'You have already been called. Please reach the counter now or you may lose your turn.'
  },
  ACTIVE: {
    tone: 'active',
    chip: 'Active',
    title: 'You are being served',
    message: 'Your queue entry is active at the counter.'
  },
  COMPLETED: {
    tone: 'completed',
    chip: 'Completed',
    title: 'Queue visit completed',
    message: 'This queue visit has already been completed.'
  },
  MISSED: {
    tone: 'missed',
    chip: 'Missed',
    title: 'Queue turn missed',
    message: 'This queue entry is no longer active. If you are physically at the store, merchant OTP rescue may still help if policy allows.'
  }
};

const DEFAULT_ALERT = ALERT_COPY.WAITING;

const getAlertPresentation = (activeQueue) => {
  const key = activeQueue?.alertState || 'WAITING';
  return ALERT_COPY[key] || DEFAULT_ALERT;
};

const getAheadSummary = (activeQueue) => {
  const aheadCount = activeQueue?.aheadCount;
  if (aheadCount == null) {
    return 'Waiting for live queue data';
  }
  if (aheadCount === 0) {
    return 'No one ahead of you';
  }
  if (aheadCount === 1) {
    return '1 person ahead';
  }
  return `${aheadCount} people ahead`;
};

const getEtaSummary = (activeQueue) => {
  const minutes = activeQueue?.estimatedWaitMinutes;
  if (!minutes) {
    return 'ETA unavailable';
  }
  if (minutes === 1) {
    return 'About 1 minute';
  }
  return `About ${minutes} minutes`;
};

const UserPortal = ({
  scanContext,
  sessionDetails,
  onScanContextFieldChange,
  onSessionDetailsFieldChange,
  onLaunchSession,
  onJoinQueue,
  activeQueue,
  activeQueueStatus,
  confirmStatus,
  onRefreshActiveQueue,
  onConfirmPresence
}) => {
  const alert = getAlertPresentation(activeQueue);
  const queueToneClass = alert.tone || 'waiting';
  const canConfirm = Boolean(activeQueue?.canConfirmPresence);
  const isConfirmSubmitting = confirmStatus?.state === 'submitting';
  const showConfirmSuccess = confirmStatus?.state === 'success';
  const showConfirmError = confirmStatus?.state === 'error';
  const confirmButtonLabel = isConfirmSubmitting
    ? 'Confirming...'
    : showConfirmSuccess && !canConfirm
      ? 'Presence confirmed'
      : 'Confirm presence';

  return (
    <section id="user-portal" className="section portal-shell">
      <div className="portal-header">
        <h1 className="portal-title">User portal</h1>
        <p className="portal-subtitle">Scan the QR code to join the queue, and wait for your turn. We&apos;ll notify you when it&apos;s time to visit the counter.</p>
        <div className="portal-header-accent"></div>
      </div>
      <div className="grid three">
        <div className="card">
          <h3>Scan QR</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Store UUID</label>
              <input className="input" value={scanContext.storeUuid} onChange={(event) => onScanContextFieldChange('storeUuid', event.target.value)} />
            </div>
            <div className="input-group">
              <label className="input-label">Mall UUID</label>
              <input className="input" value={scanContext.mallUuid} onChange={(event) => onScanContextFieldChange('mallUuid', event.target.value)} />
            </div>
            <div className="input-group">
              <label className="input-label">Section UUID</label>
              <input className="input" value={scanContext.sectionUuid} onChange={(event) => onScanContextFieldChange('sectionUuid', event.target.value)} />
            </div>
            <div className="input-group">
              <label className="input-label">Floor Number</label>
              <input className="input" value={scanContext.floorNumber} onChange={(event) => onScanContextFieldChange('floorNumber', event.target.value)} />
            </div>
          </div>
          <button className="btn primary full" onClick={onLaunchSession}>Launch session</button>
        </div>
        <div className="card">
          <h3>Enter Phone</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Phone Number</label>
              <input className="input" value={sessionDetails.phone} onChange={(event) => onSessionDetailsFieldChange('phone', event.target.value)} />
            </div>
            <div className="input-group">
              <label className="input-label">Session ID</label>
              <input className="input" value={sessionDetails.sessionId} onChange={(event) => onSessionDetailsFieldChange('sessionId', event.target.value)} />
            </div>
          </div>
          <button className="btn primary full" onClick={onJoinQueue}>Continue</button>
        </div>
        <div className="card">
          <h3>Select Counter</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Counter</label>
              <select className="input">
                <option>Counter A</option>
                <option>Counter B</option>
                <option>Counter C</option>
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Section UUID</label>
              <input className="input" value={sessionDetails.sectionUuid} onChange={(event) => onSessionDetailsFieldChange('sectionUuid', event.target.value)} />
            </div>
          </div>
          <button className="btn primary full" onClick={onJoinQueue}>Join queue</button>
        </div>
      </div>
      <div className="card portal-live-surface">
        <div className="portal-live-header">
          <div className="portal-live-note">
            <h3 className="portal-live-heading">Live Queue Status</h3>
            <p className="portal-subtitle">
              This browser view now tracks your queue position live and mirrors the server alert state. SMS and WhatsApp still serve as fallback notifications if you stop watching the page.
            </p>
          </div>
          <span className={`portal-status-chip ${queueToneClass}`}>{alert.chip}</span>
        </div>

        <div className={`portal-alert-banner ${queueToneClass}`}>
          <strong>{alert.title}</strong>
          <span>{alert.message}</span>
        </div>

        {showConfirmSuccess ? (
          <div className="portal-confirm-feedback success">
            <strong>Confirmation received</strong>
            <span>{confirmStatus.message}</span>
          </div>
        ) : null}

        {showConfirmError ? (
          <div className="portal-confirm-feedback error">
            <strong>Could not confirm yet</strong>
            <span>{confirmStatus.message}</span>
          </div>
        ) : null}

        <div className="portal-live-grid">
          <div className="portal-live-panel">
            <div className="portal-metric-grid">
              <div className="portal-metric-card">
                <div className="portal-metric-label">Ahead</div>
                <div className="portal-metric-value">{activeQueue?.aheadCount ?? '—'}</div>
                <div className="portal-metric-subtext">{getAheadSummary(activeQueue)}</div>
              </div>
              <div className="portal-metric-card">
                <div className="portal-metric-label">Estimated Wait</div>
                <div className="portal-metric-value">{activeQueue?.estimatedWaitMinutes ?? '—'}</div>
                <div className="portal-metric-subtext">{getEtaSummary(activeQueue)}</div>
              </div>
              <div className="portal-metric-card">
                <div className="portal-metric-label">Queue Position</div>
                <div className="portal-metric-value">{activeQueue?.position ?? '—'}</div>
                <div className="portal-metric-subtext">Server-reported queue position</div>
              </div>
              <div className="portal-metric-card">
                <div className="portal-metric-label">Queue Size</div>
                <div className="portal-metric-value">{activeQueue?.totalInQueue ?? '—'}</div>
                <div className="portal-metric-subtext">Active entries in this queue</div>
              </div>
            </div>
          </div>

          <div className="portal-live-panel">
            <div className="portal-queue-summary">
              <div className="portal-queue-summary-row">
                <span className="portal-queue-summary-label">Queue UUID</span>
                <span className="portal-queue-summary-value">{sessionDetails.queueUuid || 'Not joined yet'}</span>
              </div>
              <div className="portal-queue-summary-row">
                <span className="portal-queue-summary-label">Backend alert state</span>
                <span className="portal-queue-summary-value">{activeQueue?.alertState || '—'}</span>
              </div>
              <div className="portal-queue-summary-row">
                <span className="portal-queue-summary-label">Backend status</span>
                <span className="portal-queue-summary-value">{activeQueue?.status || '—'}</span>
              </div>
              <div className="portal-queue-summary-row">
                <span className="portal-queue-summary-label">Store</span>
                <span className="portal-queue-summary-value">{activeQueue?.storeName || scanContext.storeUuid || '—'}</span>
              </div>
              <div className="portal-queue-summary-row">
                <span className="portal-queue-summary-label">Section</span>
                <span className="portal-queue-summary-value">{activeQueue?.sectionName || sessionDetails.sectionUuid || scanContext.sectionUuid || '—'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="portal-queue-footer">
          <span className={`portal-queue-inline-status ${activeQueueStatus.error ? 'error' : ''}`}>
            {activeQueueStatus.error
              ? activeQueueStatus.error
              : activeQueueStatus.loading
                ? 'Refreshing live queue state...'
                : canConfirm
                  ? 'You can confirm directly from the browser when your queue state allows it.'
                  : 'Live queue polling runs every 30 seconds while this page is open.'}
          </span>
          <div className="portal-queue-actions">
            <button className="btn secondary" onClick={onRefreshActiveQueue} disabled={!sessionDetails.queueUuid || activeQueueStatus.loading}>
              {activeQueueStatus.loading ? 'Refreshing...' : 'Refresh active queue'}
            </button>
            <button
              className="btn primary"
              onClick={onConfirmPresence}
              disabled={!canConfirm || !sessionDetails.queueUuid || activeQueueStatus.loading || isConfirmSubmitting}
            >
              {confirmButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UserPortal;
